import { useState, useImperativeHandle, forwardRef } from 'react';
import { Plus, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

export interface KeyValueEntry {
  key: string;
  value: string;
}

export interface KeyValueEditorHandle {
  /**
   * Returns the in-progress draft row if both fields are filled but not yet
   * committed (the user typed a pair and hit Submit without pressing Enter or
   * the add button). Lets a parent form flush a pending entry on submit.
   */
  getPending: () => KeyValueEntry | null;
}

export interface KeyValueEditorProps {
  entries: KeyValueEntry[];
  onChange: (entries: KeyValueEntry[]) => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
  /** Renders the value as a resizable textarea and committed rows as a
   *  collapsible `key = …` summary. Use for long values like file content. */
  multilineValue?: boolean;
  /** Accessible noun for a single entry, e.g. "argument" or "file". Used to
   *  label the key/value inputs and the add/remove buttons. */
  entryLabel?: string;
}

/**
 * An inline editor for a list of `key = value` pairs (build args, env vars,
 * file mounts, …). Committed rows are inline-editable; a trailing draft row
 * adds new pairs (Enter or the add button). Key and value share width equally
 * with an `=` between them so each row reads as a single assignment.
 */
export const KeyValueEditor = forwardRef<KeyValueEditorHandle, KeyValueEditorProps>(
  function KeyValueEditor(
    {
      entries,
      onChange,
      keyPlaceholder,
      valuePlaceholder,
      multilineValue = false,
      entryLabel = 'entry',
    },
    ref,
  ) {
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
      getPending: () => {
        if (newKey.trim() && newValue.trim()) {
          return { key: newKey.trim(), value: newValue };
        }
        return null;
      },
    }));

    function handleAdd() {
      if (newKey.trim() && newValue.trim()) {
        onChange([...entries, { key: newKey.trim(), value: newValue }]);
        setNewKey('');
        setNewValue('');
      }
    }

    function handleRemove(index: number) {
      onChange(entries.filter((_, i) => i !== index));
      if (expandedIndex === index) setExpandedIndex(null);
    }

    function updateEntry(index: number, patch: Partial<KeyValueEntry>) {
      onChange(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
    }

    function toggleExpand(index: number) {
      setExpandedIndex(expandedIndex === index ? null : index);
    }

    const isMultiline = (value: string) => value.includes('\n');

    // Single-line variant (e.g. build args): every committed row is an inline
    // editable `KEY = VALUE` pair, matched by an identical draft row at the
    // bottom. Key and value share the width equally with an `=` between them so
    // it reads as one assignment instead of "a big box and a mystery box".
    if (!multilineValue) {
      return (
        <div className="space-y-1.5">
          {entries.map((entry, i) => (
            <KeyValueRow
              // Index key: rows are only added/removed at the ends, and the
              // input values are driven by `entries`, so reconciliation by
              // index is correct here and avoids remounting while the key is
              // edited.
              key={i}
              entryLabel={entryLabel}
              keyValue={entry.key}
              valueValue={entry.value}
              keyPlaceholder={keyPlaceholder}
              valuePlaceholder={valuePlaceholder}
              onKeyChange={(v) => updateEntry(i, { key: v })}
              onValueChange={(v) => updateEntry(i, { value: v })}
              trailing={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemove(i)}
                  aria-label={`Remove ${entryLabel}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              }
            />
          ))}

          <KeyValueRow
            entryLabel={`new ${entryLabel}`}
            keyValue={newKey}
            valueValue={newValue}
            keyPlaceholder={keyPlaceholder}
            valuePlaceholder={valuePlaceholder}
            onKeyChange={setNewKey}
            onValueChange={setNewValue}
            onSubmit={handleAdd}
            trailing={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                disabled={!newKey.trim() || !newValue.trim()}
                onClick={handleAdd}
                aria-label={`Add ${entryLabel}`}
              >
                <Plus className="h-4 w-4" />
              </Button>
            }
          />
        </div>
      );
    }

    // Multiline variant (e.g. file mounts): committed rows show a compact
    // `path = …` summary with expand-to-view, and the add area stacks the key
    // input above a resizable textarea.
    return (
      <div className="space-y-2">
        {entries.length > 0 && (
          <div className="space-y-1">
            {entries.map((entry, i) => {
              const hasNewlines = isMultiline(entry.value);
              const isExpanded = expandedIndex === i;

              return (
                <div key={entry.key} className="rounded border border-border bg-muted/30 text-sm">
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <span className="font-mono text-muted-foreground">{entry.key}</span>
                    <span className="text-muted-foreground">=</span>
                    {hasNewlines ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(i)}
                        className="flex-1 justify-start font-mono text-xs text-muted-foreground"
                      >
                        {isExpanded ? '▼ Collapse' : `▶ ${entry.value.split('\n').length} lines`}
                      </Button>
                    ) : (
                      <span className="flex-1 truncate font-mono">{entry.value}</span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => handleRemove(i)}
                      aria-label={`Remove ${entryLabel}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {hasNewlines && isExpanded && (
                    <pre className="border-t border-border bg-muted/50 px-2 py-2 font-mono text-xs whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {entry.value}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="space-y-2">
          <Input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder={keyPlaceholder}
            aria-label={`New ${entryLabel} key`}
            className="h-8 px-2.5 font-mono"
          />
          <Textarea
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={valuePlaceholder}
            aria-label={`New ${entryLabel} value`}
            className="resize-y min-h-[80px] px-2.5 font-mono"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={!newKey.trim() || !newValue.trim()}
            onClick={handleAdd}
          >
            <Plus className="size-4" />
            Add {entryLabel}
          </Button>
        </div>
      </div>
    );
  },
);

/** One inline `KEY = VALUE` row: two equal-width monospace inputs joined by an
 *  `=`, with a caller-supplied trailing action (remove or add). */
function KeyValueRow({
  entryLabel,
  keyValue,
  valueValue,
  keyPlaceholder,
  valuePlaceholder,
  onKeyChange,
  onValueChange,
  onSubmit,
  trailing,
  className,
}: {
  entryLabel: string;
  keyValue: string;
  valueValue: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  onKeyChange: (value: string) => void;
  onValueChange: (value: string) => void;
  onSubmit?: () => void;
  trailing: React.ReactNode;
  className?: string;
}) {
  function handleKeyDown(e: React.KeyboardEvent) {
    if (onSubmit && e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Input
        value={keyValue}
        onChange={(e) => onKeyChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={keyPlaceholder}
        aria-label={`${entryLabel} name`}
        className="h-8 min-w-0 flex-1 px-2.5 font-mono text-sm"
      />
      <span className="shrink-0 select-none font-mono text-sm text-muted-foreground">=</span>
      <Input
        value={valueValue}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={valuePlaceholder}
        aria-label={`${entryLabel} value`}
        className="h-8 min-w-0 flex-1 px-2.5 font-mono text-sm"
      />
      {trailing}
    </div>
  );
}
